import { useEffect } from "react";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    __mtliTracking?: boolean;
  }
}

/**
 * Behavior tracking into GTM/GA4 (which adds visitor geo automatically):
 * every link/button click, form starts, and form abandonment.
 */
const BehaviorTracking = () => {
  useEffect(() => {
    if (typeof window === "undefined" || window.__mtliTracking) return;
    window.__mtliTracking = true;
    const push = (event: string, params: Record<string, unknown>) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...params });
    };

    document.addEventListener(
      "click",
      (e) => {
        const el = (e.target as HTMLElement | null)?.closest("a,button");
        if (!el) return;
        push("site_click", {
          click_text: (el.textContent || "").trim().slice(0, 80),
          click_href: el.getAttribute("href") || "",
          click_page: window.location.pathname,
        });
      },
      { capture: true, passive: true }
    );

    const startedForms = new Set<HTMLFormElement>();
    const submittedForms = new Set<HTMLFormElement>();
    document.addEventListener(
      "input",
      (e) => {
        const form = (e.target as HTMLElement | null)?.closest("form");
        if (!form || startedForms.has(form)) return;
        startedForms.add(form);
        push("form_start", { form_page: window.location.pathname });
      },
      { capture: true, passive: true }
    );
    document.addEventListener(
      "submit",
      (e) => {
        const form = e.target as HTMLFormElement | null;
        if (form) submittedForms.add(form);
        push("form_submit", { form_page: window.location.pathname });
      },
      { capture: true, passive: true }
    );
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "hidden") return;
      startedForms.forEach((form) => {
        if (!submittedForms.has(form)) {
          push("form_abandon", { form_page: window.location.pathname });
        }
      });
    });
  }, []);
  return null;
};

export default BehaviorTracking;
